import { LEVEL_1, LEVEL_2, LEVEL_3, WEAPONS, PICKUP_AHEAD, BLAST_REACH_Z, BLASTS, SQUAD_FOOTPRINT_HALF_X, steerLimit, validateLevel, type LevelSpec } from './level';
import { ENCOUNTERS, placeEncounter, type EncounterFamily, type EncounterPhase, type EncounterVariant } from './encounters';
import { createSim, type SimState } from './sim';

export type ExpeditionRoute = 'recruitment' | 'armory';
export type ExpeditionLoadout = { n: number; weaponTier: number; ratePips: number };
export type EncounterSelection = { phase: EncounterPhase; family: EncounterFamily | 'choice'; variant: string; mirrored: boolean };
export type ExpeditionGeneration = { level: LevelSpec; encounters: EncounterSelection[]; pressure: number; fallbackReason: string | null };
export const MIN_GATE_ENEMY_GAP = 30;

export function validateExpedition(level: LevelSpec): string[] {
  const errors = validateLevel(level);
  if (level.gates.length < 6 || level.gates.length > 8 || level.enemies.length > 4 || level.hazards.length > 3 || level.crates.length > 2 || level.obstacles.length > 6) errors.push('content ceiling');
  for (const e of level.enemies) {
    const preceding = level.gates.filter(g => g.z < e.z).at(-1);
    if (preceding && e.z - preceding.z < MIN_GATE_ENEMY_GAP) errors.push(`enemy at ${e.z} too close to gate`);
    if (e.z + PICKUP_AHEAD >= 250) errors.push('enemy pickup enters recovery');
    if (Math.abs(e.x) - e.halfWidth - SQUAD_FOOTPRINT_HALF_X <= -steerLimit(SQUAD_FOOTPRINT_HALF_X)) errors.push(`no full-width enemy bypass at ${e.z}`);
  }
  const legal = steerLimit(SQUAD_FOOTPRINT_HALF_X);
  for (const h of [...level.hazards, ...level.obstacles]) {
    if (Math.abs(h.x) - h.halfWidth - SQUAD_FOOTPRINT_HALF_X <= -legal) errors.push(`no full-width bypass at ${h.z}`);
    if (h.z >= 250) errors.push('threat enters recovery');
    if ('kind' in h && h.kind === 'barrel') {
      const blast = BLASTS.barrel!;
      if (Math.abs(h.x) - blast.halfWidth - SQUAD_FOOTPRINT_HALF_X <= -legal) errors.push(`no full-width blast bypass at ${h.z}`);
      if (h.z + BLAST_REACH_Z >= 250) errors.push('blast enters recovery');
    }
  }
  const threats = [...level.enemies, ...level.hazards, ...level.obstacles].sort((a, b) => a.z - b.z);
  for (let i = 1; i < threats.length; i++) {
    const previous = threats[i - 1]!;
    const current = threats[i]!;
    // Include a novice reaction delay and the time to cross between both legal rails.
    if (Math.sign(previous.x) !== Math.sign(current.x) &&
      current.z - previous.z < level.speed * (0.25 + 2 * legal / level.lateralSpeed)) {
      errors.push(`insufficient bypass transition at ${current.z}`);
    }
  }
  if (level.enemies.some(e => e.z < 50) || [...level.hazards, ...level.obstacles].some(t => t.z < 60)) errors.push('threat enters introduction too early');
  if (level.crates.some(c => c.z >= 250) || level.gates.some(g => g.z > 350)) errors.push('pickup or gate enters boss preparation');
  return errors;
}

export function generateExpedition(seed: string, depth: number, route: ExpeditionRoute): ExpeditionGeneration {
  const stage = Number.isFinite(depth) ? Math.min(1_000_000, Math.max(1, Math.floor(depth))) : 1;
  let state = 2166136261;
  for (const c of `${seed}:${stage}:${route}`) state = Math.imul(state ^ c.charCodeAt(0), 16777619);
  const next = (n: number): number => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) % n;
  };
  // shortcut: late-depth pressure is uncalibrated, playtest longer runs before tuning it.
  const pressure = Math.min(stage - 1, 30) + 7.5 * Math.log2(1 + Math.max(0, stage - 31) / 30);
  const encounterPressure = 1 + Math.min(1, pressure / 15);
  const base = [LEVEL_1, LEVEL_2, LEVEL_3][(stage - 1) % 3]!;
  const build = (selected: EncounterVariant[], mirrors: boolean[]): ExpeditionGeneration => {
    const [first, second, calm] = selected as [EncounterVariant, EncounterVariant, EncounterVariant];
    const segments = [placeEncounter(first, 60, mirrors[0]!, encounterPressure), placeEncounter(second, 160, mirrors[1]!, encounterPressure), placeEncounter(calm, 250, mirrors[2]!, 1)];
    // Four short firefights, rather than seeds with no squads at all. Supply and shortcut
    // lanes get a guard; the opening patrol and second wave leave recovery untouched.
    const patrol = (z: number, mirrored: boolean, count: number) => ({
      z, x: mirrored ? 0.65 : -0.65, halfWidth: 0.3,
      count: Math.round(count * encounterPressure), killInterval: 0, dropReward: false,
    });
    for (let i = 0; i < 2; i++) {
      if (!segments[i]!.enemies.length) segments[i]!.enemies.push(patrol(i === 0 ? 105 : 205, mirrors[i]!, 15));
    }
    const enemies = [patrol(52, mirrors[0]!, 10), ...segments.flatMap(s => s.enemies), patrol(132, mirrors[0]!, 10)]
      .map(e => ({ ...e, dropReward: false })).sort((a, b) => a.z - b.z);
    const opening: LevelSpec['gates'][number] = { z: 20, left: { kind: 'num', value: 5 }, right: { kind: 'div', value: 2 } };
    if (mirrors[0]) [opening.left, opening.right] = [opening.right, opening.left];
    const boost = segments[0]!.gates[0]!;
    boost[mirrors[0] ? 'right' : 'left'] = { kind: 'mul', value: 2 };
    boost[mirrors[0] ? 'left' : 'right'] = { kind: 'num', value: 10 };
    // Commit fire to reduce the large subtraction, or take the predictable half-squad exit.
    const final: LevelSpec['gates'][number] = { z: 340, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: -200 } };
    if (mirrors[2]) [final.left, final.right] = [final.right, final.left];
    const gates = [opening, ...segments.flatMap(s => s.gates), final].sort((a, b) => a.z - b.z);
    // One obtainable cache per bridge keeps weapon timing legible; combat groups do not
    // also shower free tiers onto the same build. Supply encounters own their cache.
    const crates = segments.flatMap(s => s.crates);
    if (!crates.length) crates.push({ z: 45, hp: 6, x: mirrors[0] ? 0.45 : -0.45 });
    const level: LevelSpec = {
      name: `Expedition ${stage}`, startN: 20, speed: 10, lateralSpeed: 2.2,
      gates,
      crates: crates.sort((a, b) => a.z - b.z),
      enemies,
      hazards: segments.flatMap(s => s.hazards).sort((a, b) => a.z - b.z),
      obstacles: segments.flatMap(s => s.obstacles).sort((a, b) => a.z - b.z),
      boss: { ...base.boss, z: 418, hp: Math.round([stage === 1 ? 1300 : 3600, 1800, 1600][(stage - 1) % 3]! * (1 + pressure * 0.04)), slamDamage: 28 + Math.floor(pressure * 18), fairAttackZones: true },
      weapons: WEAPONS,
    };
    const encounters: EncounterSelection[] = [
      { phase: 'introduction', family: 'choice', variant: 'opening-patrol', mirrored: mirrors[0]! },
      ...selected.map((v, i) => ({ phase: (['complication', 'payoff', 'recovery'] as const)[i]!, family: v.family, variant: v.id, mirrored: mirrors[i]! })),
      { phase: 'boss-preparation', family: 'choice', variant: 'final-choice', mirrored: mirrors[2]! },
    ];
    return { level, encounters, pressure, fallbackReason: null };
  };
  const active = ENCOUNTERS.filter(v => v.phases.includes('complication'));
  const weighted = [...active, ...active.filter(v => v.family === (route === 'recruitment' ? 'rescue' : 'supply'))];
  for (let attempt = 0; attempt < 16; attempt++) {
    const first = weighted[next(weighted.length)]!;
    const options = weighted.filter(v => v.family !== first.family);
    const second = options[next(options.length)]!;
    const calm = ENCOUNTERS.filter(v => v.family === 'recovery')[next(2)]!;
    const candidate = build([first, second, calm], [!!next(2), !!next(2), !!next(2)]);
    if (!validateExpedition(candidate.level).length) return candidate;
  }
  const fallback = build([ENCOUNTERS[0]!, ENCOUNTERS[6]!, ENCOUNTERS[8]!], [false, false, false]);
  const fallbackErrors = validateExpedition(fallback.level);
  if (fallbackErrors.length) throw new Error(`Invalid expedition fallback: ${fallbackErrors.join(', ')}`);
  fallback.fallbackReason = 'No valid candidate after 16 attempts';
  return fallback;
}

export function generateExpeditionLevel(seed: string, depth: number, route: ExpeditionRoute): LevelSpec {
  return generateExpedition(seed, depth, route).level;
}

export function createExpeditionSim(level: LevelSpec, loadout: ExpeditionLoadout): SimState {
  const clamp = (value: number, min: number, max: number): number =>
    Number.isFinite(value) ? Math.max(min, Math.min(max, Math.floor(value))) : min;
  const sim = createSim(level);
  sim.n = clamp(loadout.n, 1, 10000);
  sim.crowdFrom = sim.n;
  sim.weaponTier = clamp(loadout.weaponTier, 0, Math.min(3, level.weapons.length - 1));
  sim.ratePips = clamp(loadout.ratePips, 0, 2);
  return sim;
}
